import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useAppDispatch, useAppSelector } from 'src/store/hooks';
import { selectAuthStatus, setAuthStatus } from 'src/features/login/authSlice';
import { useGetCurrentSessionQuery } from 'src/api/authApi';
import { setAuthorize, getAuthorizes, removeAuthorize } from 'src/storage/authorize';
import { routePathConfig } from 'src/routes/config';
import { OAUTH_PARAMS } from 'src/constants/oauth';

interface UseExistingSessionOptions {
  enabled: boolean;
}

/**
 * Skip the password prompt when the browser already holds an SSO session.
 *
 * The session is an HttpOnly cookie, so this app cannot inspect it — it asks
 * `GET /security/session`, which is the only authority. A 401 there is an
 * ordinary answer meaning "no session", not an error to surface. Previously this
 * read a `user_pass` out of localStorage, which is exactly the exposure ticket
 * 11 removed.
 *
 * On success the user goes to the consent page (preserving the original
 * `?client_id=...&redirect_uri=...` query string so GrantConsent can read them)
 * or, with no `client_id`, to the account management landing page.
 */
export const useExistingSession = ({ enabled }: UseExistingSessionOptions): void => {
  const dispatch = useAppDispatch();
  const authStatus = useAppSelector(selectAuthStatus);
  const navigate = useNavigate();
  const location = useLocation();

  const { data, isSuccess, isError } = useGetCurrentSessionQuery(undefined, {
    skip: !enabled || authStatus === 'authenticated',
    // The cookie can expire or be cleared without this app knowing, so never
    // answer from cache when the login page mounts.
    refetchOnMountOrArgChange: true,
  });

  useEffect(() => {
    if (!enabled || !isError || authStatus === 'authenticated') {
      return;
    }
    const stale = getAuthorizes()[0];
    if (stale) {
      removeAuthorize(stale.account);
      dispatch(setAuthStatus('expired'));
    }
  }, [enabled, isError, authStatus, dispatch]);

  useEffect(() => {
    if (!enabled || !isSuccess || !data) {
      return;
    }

    setAuthorize({
      account: data.account,
      expiresAt: Math.floor(Date.now() / 1000) + data.expiresIn,
    });
    dispatch(setAuthStatus('authenticated'));

    const hasClientId = Boolean(new URLSearchParams(location.search).get(OAUTH_PARAMS.CLIENT_ID));
    navigate({
      pathname: hasClientId ? routePathConfig.grantConsent : routePathConfig.landing,
      search: location.search,
    });
  }, [enabled, isSuccess, data, dispatch, navigate, location.search]);
};
